import { z } from 'zod'

import {
  billingPeriodSchema,
  bytesSchema,
  idSchema,
  invoiceMethodSchema,
  invoiceStatusSchema,
  isoDateSchema,
  isoInstantSchema,
  labelSchema,
  limitSchema,
  moneyPaisa,
  noteSchema,
  nonNegativePaisaSchema,
  planCodeSchema,
  planFeatureSchema,
  ratioSchema,
  slugSchema,
  sortSchema,
} from '../common'
import { cursorQuerySchema } from '../pagination'

/**
 * P9–P12 · Plans, subscriptions, invoices.
 *
 * **No card is stored in v1** (D3): no PAN, no gateway token, no secret. A
 * payment arrives by JazzCash, Easypaisa or bank transfer and a human records
 * it, which is why P11 exists and why every invoice carries who marked it paid.
 */

/* ── P9 · GET/POST/PATCH /platform/plans ──────────────────────────────────── */

export const planSchema = z.object({
  id: idSchema,
  code: planCodeSchema,
  name: z.string(),
  pricePaisa: moneyPaisa(),
  billingPeriod: billingPeriodSchema,
  /** `null` is unlimited — not zero, which would mean "none allowed". */
  maxAnimals: limitSchema,
  maxUsers: limitSchema,
  maxStorageBytes: limitSchema,
  features: z.array(planFeatureSchema),
  isPublic: z.boolean(),
  sortOrder: z.number().int(),
  trialDays: z.number().int().nonnegative().nullish(),
  /** How many books are on it, and what it brings in. Read-only. */
  businesses: z.number().int().nonnegative().optional(),
  revenuePaisa: moneyPaisa(),
})

/**
 * What a plan is made of, with NO defaults on any of it.
 *
 * The defaults belong to `createPlanRequest` alone, and they are added there
 * rather than here for a reason that cost real damage:
 *
 * `patchPlanRequest` used to be `createPlanRequest.partial()`, and **`.partial()`
 * does not remove a `.default()`** — it makes the key optional and then fills it
 * anyway. So a PATCH carrying only `{ pricePaisa }` arrived at the service as
 * `{ pricePaisa, billingPeriod: 'MONTHLY', features: [], isPublic: true,
 * sortOrder: 0 }`, and `patchPlan` writes every key that is not `undefined`.
 * Changing a plan's price therefore **emptied its feature list**, reset its
 * billing period and made it public — and `plans.features` is what
 * `loadTenantRuntime` carries onto every request for `requireFeature` to read,
 * so every book on that plan silently lost its Owner Console, Reports, Price
 * Lock, Lots and partner shares.
 *
 * A patch schema must therefore never be derived from a schema that has
 * defaults. Both are now built from this one, which has none.
 */
const planFields = {
  code: planCodeSchema,
  name: labelSchema,
  pricePaisa: nonNegativePaisaSchema,
  billingPeriod: billingPeriodSchema,
  maxAnimals: limitSchema,
  maxUsers: limitSchema,
  maxStorageBytes: limitSchema,
  features: z.array(planFeatureSchema),
  isPublic: z.boolean(),
  sortOrder: z.number().int(),
  trialDays: z.number().int().nonnegative().optional(),
}

export const createPlanRequest = z.object({
  ...planFields,
  billingPeriod: billingPeriodSchema.default('MONTHLY'),
  features: z.array(planFeatureSchema).default([]),
  isPublic: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
})

/**
 * Every field of a plan is changeable, `code` included.
 *
 * `code` used to be omitted, and the console consequently offered a single
 * field — the amount — so a plan named or limited wrongly on the day it was
 * created stayed that way. It is editable now, but it is still
 * `planCodeSchema`: a CLOSED enum of the four codes, deliberately, because the
 * code is compared as a string in three places that would not be reading this
 * schema when it changed — `usage.service.ts` (which plan to suggest),
 * `tenantUsage.repository.ts` (`coalesce(plans.code, 'TRIAL')`) and the
 * trader's app. Free text there would not be an editable field, it would be a
 * way to make a live book's plan unresolvable. Widening `PLAN_CODES` is a
 * contract change and a migration, and belongs with them rather than here.
 */
export const patchPlanRequest = z
  .object(planFields)
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

export const planListResponse = z.array(planSchema)

/* ── P10 · GET /platform/subscriptions — screen 07 ────────────────────────── */

/**
 * The overdue table is not a list, it is a worklist: each row carries the one
 * action it is actually for. Dunning retries after **3 days, never the same
 * day** — nearly every failure here is an empty wallet, not a refusal, and
 * retrying an empty wallet an hour later just burns the relationship.
 */
export const OVERDUE_ACTIONS = ['CALL_SUSPEND', 'CALL_REMIND', 'RETRY_REMIND', 'LEAVE_IT'] as const

export const overdueRowSchema = z.object({
  businessId: idSchema,
  slug: slugSchema,
  name: z.string(),
  city: z.string().nullish(),
  animals: z.number().int().nonnegative(),
  planCode: planCodeSchema,
  invoiceId: idSchema,
  amountPaisa: moneyPaisa(),
  lateDays: z.number().int().nonnegative(),
  lastTriedAt: isoInstantSchema.nullish(),
  failureReason: z.string().nullish(),
  action: z.enum(OVERDUE_ACTIONS),
})

export const subscriptionsSummaryResponse = z.object({
  asOf: isoDateSchema,
  collectedPaisa: moneyPaisa(),
  paidCount: z.number().int().nonnegative(),
  payingCount: z.number().int().nonnegative(),
  overduePaisa: moneyPaisa(),
  overdueCount: z.number().int().nonnegative(),
  overdueShare: ratioSchema.optional(),
  renewingPaisa: moneyPaisa(),
  renewingCount: z.number().int().nonnegative(),
  trialsEnding: z.number().int().nonnegative(),
  overdue: z.array(overdueRowSchema),
  renewals: z.array(
    z.object({
      date: isoDateSchema,
      businessId: idSchema,
      name: z.string(),
      amountPaisa: moneyPaisa(),
    }),
  ),
  moreRenewals: z.object({
    count: z.number().int().nonnegative(),
    amountPaisa: moneyPaisa(),
  }),
  failureReasons: z.array(
    z.object({
      reason: z.string(),
      count: z.number().int().nonnegative(),
      of: z.number().int().nonnegative(),
    }),
  ),
})

export const subscriptionListQuery = cursorQuerySchema.extend({
  status: z.enum(['OVERDUE', 'RENEWING', 'TRIALING', 'ACTIVE', 'CANCELLED']).optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  sort: sortSchema(['-lateDays', 'nextChargeOn', 'name'] as const, '-lateDays'),
})

/* ── invoices, P11–P12 ────────────────────────────────────────────────────── */

export const invoiceSchema = z.object({
  id: idSchema,
  number: z.string(),
  tenantId: idSchema,
  tenantName: z.string().optional(),
  periodStart: isoDateSchema,
  periodEnd: isoDateSchema,
  amountPaisa: moneyPaisa(),
  status: invoiceStatusSchema,
  issuedOn: isoDateSchema,
  paidOn: isoDateSchema.nullish(),
  method: invoiceMethodSchema.nullish(),
  failureReason: z.string().nullish(),
  retryCount: z.number().int().nonnegative(),
  nextRetryOn: isoDateSchema.nullish(),
  /** D3 · who marked it paid. A manual confirmation needs a name against it. */
  recordedByName: z.string().nullish(),
})

/**
 * P11 · Manual confirmation (D3). The amount is optional: a partial payment is
 * recorded as what actually arrived, not as the invoice total.
 */
export const recordInvoicePaymentRequest = z.object({
  method: invoiceMethodSchema,
  paidOn: isoDateSchema,
  amountPaisa: nonNegativePaisaSchema.optional(),
  reference: z.string().trim().max(120).optional(),
  note: noteSchema.optional(),
})

export const invoiceListResponse = z.array(invoiceSchema)

/** P12 · Retry the charge, or send a reminder. Both are audited actions. */
export const remindInvoiceRequest = z.object({
  channel: z.enum(['WHATSAPP', 'SMS', 'EMAIL']).default('WHATSAPP'),
  note: noteSchema.optional(),
})

/** The payment-mix tile: which rails the businesses actually pay on. */
export const paymentMixResponse = z.array(
  z.object({
    method: invoiceMethodSchema,
    businesses: z.number().int().nonnegative(),
    amountPaisa: moneyPaisa(),
  }),
)

/**
 * `amountPaisa` is optional like every other money field, so an actor without
 * `platform.billing` receives the row with the key deleted rather than zeroed —
 * which is why `businesses` is deliberately NOT money: how many traders pay by
 * bank transfer is an operational fact support needs, and nothing about it
 * discloses the platform's revenue.
 */

/** Storage is priced in bytes; the console shows it beside the money. */
export const storageBytes = bytesSchema

export type Plan = z.infer<typeof planSchema>
export type CreatePlanRequest = z.input<typeof createPlanRequest>
export type PatchPlanRequest = z.input<typeof patchPlanRequest>
export type SubscriptionsSummaryResponse = z.infer<typeof subscriptionsSummaryResponse>
export type SubscriptionListQuery = z.input<typeof subscriptionListQuery>
export type Invoice = z.infer<typeof invoiceSchema>
export type PaymentMixRow = z.infer<typeof paymentMixResponse>[number]
export type PaymentMixResponse = z.infer<typeof paymentMixResponse>
export type RecordInvoicePaymentRequest = z.input<typeof recordInvoicePaymentRequest>
export type RemindInvoiceRequest = z.input<typeof remindInvoiceRequest>
